import { useIntervalFn, useNow } from '@vueuse/core';
import moment from 'moment';
import { computed, inject, onScopeDispose, provide, shallowRef, unref, watch } from 'vue-demi';
import { useCurrentAccount, useCurrentUser } from '@/api';
import { useLaunchDarkly } from './useLaunchDarkly';
import { useTeamworkFeaturesState } from './useTeamworkFeatures';

const useFeaturesSymbol = Symbol('useFeatures');

/**
 * Related to Craic Dealer experiment
 * @param {string | null | undefined} value
 */
function normalizeFeatureLimit(value) {
  if (value === null || value === '' || value === '-1') {
    return Infinity;
  }
  if (value === undefined) {
    return 0;
  }
  return Number(value);
}

/**
 * Provides LaunchDarkly and Teamwork features.
 */
function FeaturesService() {
  const account = useCurrentAccount();
  const user = useCurrentUser();
  const nowDate = useNow({ interval: 60 * 60 * 1000 });
  const now = computed(() => moment(nowDate.value));

  const { items: twFeaturesList, inSync: twFeaturesInSync } = useTeamworkFeaturesState();
  const twFeaturesInitialized = shallowRef(false);
  const twFeatures = computed(() => {
    const features = new Map();
    twFeaturesList.value.forEach((feature) => {
      features.set(feature.key, feature);
    });
    return features;
  });
  const unwatch = watch(
    twFeaturesInSync,
    () => {
      if (twFeaturesInSync.value) {
        twFeaturesInitialized.value = true;
        queueMicrotask(() => unwatch());
      }
    },
    { immediate: true },
  );

  const { client: ldClient, ready: ldReady, initialized: ldInitialized } = useLaunchDarkly();
  const launchDarklyUpdateCount = shallowRef(0);
  let removeChangeListener;

  function triggerLaunchDarklyUpdate() {
    launchDarklyUpdateCount.value += 1;
  }

  watch([ldReady, ldInitialized], triggerLaunchDarklyUpdate);

  function addChangeListener() {
    const client = ldClient.value;
    if (client) {
      client.on('change', triggerLaunchDarklyUpdate);
      return () => client.off('change', triggerLaunchDarklyUpdate);
    }
    return undefined;
  }

  watch(
    ldClient,
    () => {
      removeChangeListener?.();
      removeChangeListener = addChangeListener();
    },
    { immediate: true },
  );

  // Make sure that the calls to the LaunchDarkly `track` function are recorded,
  // even if the app keeps running for more than 24 hours.
  // See https://docs.launchdarkly.com/home/experimentation/interpreting#understanding-conversions
  useIntervalFn(triggerLaunchDarklyUpdate, 24 * 60 * 60 * 1000);

  onScopeDispose(() => {
    removeChangeListener?.();
    removeChangeListener = undefined;
  });

  // Returns a `computed` which provides a feature flag value from LaunchDarkly.
  function ldFlag(name, defaultValue, overrideValue) {
    return computed(() => {
      if (unref(overrideValue) !== undefined) {
        return unref(overrideValue);
      }
      // Accessing `launchDarklyUpdateCount.value` retriggers the computed when the flag
      // value must be retrieved from LaunchDarkly again.
      if (ldClient.value && launchDarklyUpdateCount.value >= 0) {
        return ldClient.value.variation(name, unref(defaultValue));
      }
      return unref(defaultValue);
    });
  }

  // Returns a `computed` which indicates if a Teamwork feature is enabled.
  /** @returns {{value: boolean}} */
  function twEnabled(name, overrideValue) {
    return computed(() => {
      if (unref(overrideValue) !== undefined) {
        return unref(overrideValue);
      }
      const feature = twFeatures.value.get(name);
      return Boolean(
        feature &&
          (feature.startAt == null || now.value.isAfter(feature.startAt)) &&
          (feature.endAt == null || now.value.isBefore(feature.endAt)) &&
          (feature.scope !== 'ownercompany' || user.value?.inOwnerCompany) &&
          // feature.scope === 'nobody' means "disabled in beta, but potentially togglable"
          feature.scope !== 'nobody',
      );
    });
  }

  // Returns a `computed` which indicates if a Teamwork AI feature is enabled.
  /** @returns {{value: boolean}} */
  function twAiEnabled(name, overrideValue) {
    return computed(() => {
      if (unref(overrideValue) !== undefined) {
        return unref(overrideValue);
      }
      if (!account.value?.ai?.enabled) {
        return false;
      }
      if (!name) {
        return true;
      }
      if (!account.value.ai.features[name]) {
        console.warn('Teamwork AI feature not found:', name);
        return false;
      }
      return account.value.ai.features[name].enabled;
    });
  }

  /**
   * Returns a `computed` which evaluates to the value of a Teamwork feature.
   * @template T
   * @param {string} name
   * @param {(value: string | null | undefined) => T} normalize
   * @param {T | import('vue').Ref<T>} [overrideValue]
   * @returns {import('vue').ComputedRef<T>}
   */
  function twValue(name, normalize, overrideValue) {
    return computed(() => {
      if (overrideValue !== undefined) {
        return unref(overrideValue);
      }
      const feature = twFeatures.value.get(name);
      return normalize(feature?.value);
    });
  }

  return {
    initialized: computed(() => ldInitialized.value && twFeaturesInitialized.value),

    // LaunchDarkly features
    dashboardHomeSideNavEnabled: ldFlag('projects-dashboard-sidenav-dashboard', false),
    inboxGroupingEnabled: ldFlag('projects-inbox-grouping', false),
    hotspotTourEnabled: ldFlag('projects-hotspot-tour-flag', false),
    hideUpgradePathsForMaxpSmallAccountsEnabled: ldFlag('projects-hide-upgrade-paths-maxp-small-accounts', false),
    addUsersCheckoutModalEnabled: ldFlag('projects-show-add-users-checkout-modal', false),
    checkoutV2Enabled: ldFlag('platform-checkout-v2', false),
    projectsProfilePhotoPromptEnabled: ldFlag('projects-profile-photo-prompt-experiment', false),
    inviteUsersAsAdminsEnabled: ldFlag('projects-invite-users-as-admins', false),
    xeroExportEnabled: ldFlag('projects-xero-export-refresh', false),
    teamworkNextNotebooksEnabled: ldFlag('teamwork-next-notebooks', false),
    deskProjectTicketLinkEnabled: ldFlag('desk-project-ticket-link', false),
    deskOnboardingFlowEnabled: ldFlag('desk-onboarding-flow', false),
    projectsCompanyMultiCurrencyEnabled: ldFlag('projects-company-multi-currency', false),
    projectsEmailCommentsEnabled: ldFlag('projects-email-comments', false),
    projectsOnboardingDummyExperimentVariation: ldFlag('projects-onboarding-dummy-experiment', 'control'),
    isNewInlineImageEnabled: ldFlag('projects-new-inline-image', false),
    noCustomReportingRefreshLimit: ldFlag('projects-custom-reporting-reduced-cutoff', false),
    timeTotalsV3Enabled: ldFlag('projects-time-totals-v3', false),
    hideOldWorkload: ldFlag('projects-only-new-workload', false),
    newUserProfileEnabled: ldFlag('projects-lightspeed-new-user-profile', false),
    newLogTimeModalEnabled: ldFlag('projects-new-log-time-modal', false),
    hideBoardTriggersEnabled: ldFlag('projects-hide-board-triggers-from-users', false),
    welcomePageV3Enabled: ldFlag('projects-exp-a-23-43-welcome-page-v3', false),
    customEmojiReactionsEnabled: ldFlag('projects-custom-emoji-reactions', false),
    projectsExportXeroDialogEnabled: ldFlag('projects-export-xero-dialog', false),
    timeExportV3Enabled: ldFlag('projects-time-export-v3', false),
    projectsFinanceOnboardingV2Enabled: ldFlag('projects-exp-a-23-01-finance-onboarding-v2', false),
    projectsDeliverToGrowSurfaceUpgradePaths: ldFlag('projects-exp-e-23-30-dg-upgrade-paths', false),
    projectsOnboardingTemplatesExperimentEnabled: ldFlag('projects-exp-a-23-26-onboarding-templates', false),
    projectsGoalBasedOnboardingExperimentEnabled: ldFlag('projects-exp-a-23-50-goal-based-onboarding', false),
    projectsGoalBasedOnboardingBlankStatesEnabled: ldFlag('projects-exp-a-23-50-gbo-blank-states', false),
    projectsTimelogRequiresTaskEnabled: ldFlag('projects-timelog-requires-task-enabled', false),
    onboardingGoalsSelectionRedesignEnabled: ldFlag('projects-exp-23-37-onboarding-goals-selection-redesign', false),
    projectsDummyDataEnabled: ldFlag('projects-exp-a-23-27-dummy-data', false),
    projectsDummyDataV3Enabled: ldFlag('projects-exp-a-23-27-dummy-data-v3', false),
    projectsFeatureTrialScale: ldFlag('projects-exp-e-23-21a-feature-trials-scale', false),
    projectsFeatureTrialGrow: ldFlag('projects-exp-e-23-21b-feature-trials-grow', false),
    autoPremiumFeatureTrialsEnabled: ldFlag('projects-exp-l-14-07-auto-premium-feature-trials', false),
    slackIntegrationDuringOnboarding: ldFlag('projects-exp-a-23-41-slack-integration-during-onboarding', false),
    onboardingSignupSourceSurveyStepEnabled: ldFlag('projects-g-23-07-onboarding-signup-source-survey', false),
    projectsTemplateGalleryEnabled: ldFlag('projects-template-gallery', false),
    projectsCraicDealerMaxProjects: ldFlag('projects-exp-e-23-01-craic-dealer-maxp', false),
    projectsWorkflowsEnabled: ldFlag('projects-workflows', false),
    promoteGrowPlanDuringTrialEnabled: ldFlag('projects-exp-a-23-57-promote-grow-plan-during-trial', false),
    addClientCreationToOnboardingFlowEnabled: ldFlag(
      'projects-exp-a-23-62-add-client-creation-to-onboarding-flow',
      false,
    ),
    projectRetainerTemplateFlowEnabled: ldFlag('projects-exp-e-23-32-retainer-template-flow', false),
    budgetsUxImprovements: ldFlag('projects-exp-a-23-63-budgets-ux-improvements', false),
    projectsGlobalWorkflowsEnabled: ldFlag('projects-global-workflows', false),
    defaultLandingPageAfterOnboardingEnabled: ldFlag(
      'projects-exp-a-24-01-default-landing-page-after-onboarding',
      false,
    ),
    displayAccountExecContacDetailsToTrialsEnabled: ldFlag(
      'projects-exp-a-24-08-display-account-exec-contac-details-to-trials',
      false,
    ),
    rolesManyToManyEnabled: ldFlag('projects-roles-many-to-many', false),
    projectsFixedFeeBudgetsEnabled: ldFlag('projects-fixed-fee-budgets', false),
    projectsClientTicketsEnabled: ldFlag('projects-client-tickets', false),
    projectsNewTaskListBudgetsEnabled: ldFlag('projects-task-list-budgets-dialog', false),
    projectsFixedFeePermissionEnabled: ldFlag('projects-fixed-fee-permission', false),
    projectsBudgetProfitabilityPermissionEnabled: ldFlag('projects-budget-profitability-permission', false),
    projectsIntegrationsSalesforceEnabled: ldFlag('projects-integrations-salesforce', false),
    projectsPreventTaskDeletion: ldFlag('projects-prevent-task-deletion', false),
    rtuDelay: ldFlag('projects-twa-rtu-delay', 5000),
    projectsAddSeatsStandardUsersEnabled: ldFlag('projects-exp-e-25-02-add-seats-standard-users', false),
    projectsTaskTimelineEnabled: ldFlag('projects-task-timeline', false),
    projectsShowNewWorkloadAvailabilityEnabled: ldFlag('projects-show-new-workload-availability', false), // Owner: Planning
    projectsPlanningExcludeTagsEnabled: ldFlag('projects-planning-exclude-tags', false), // Owner: Planning
    projectsGlobalPlaceholdersEnabled: ldFlag('projects-global-placeholders', false),
    privacyEnabled: ldFlag('projects-privacy', false), // Owner Core
    billableToggleRestrictedToAdminsEnabled: ldFlag('projects-billable-toggle-restricted-to-admins', false), // Owner Manuel Cuesta / Financial Times
    verifiedTimelogColumnEnabled: ldFlag('projects-verified-timelog-column', false),

    // importok flags
    importokUsersEnabled: ldFlag('projects-importok-users', false),
    importokProjectsEnabled: ldFlag('projects-importok-projects', false),
    importokTimeEnabled: ldFlag('projects-importok-time', false),

    // Teamwork features
    activeProjectLimit: twValue('activeprojectlimit', normalizeFeatureLimit),
    activeProjectLimitEnabled: twEnabled('activeprojectlimit'),
    advancedFinancialIntegrationsEnabled: twEnabled('advancedfinancialintegrations'),
    auditTrailEnabled: twEnabled('audittrail'),
    billableTargetMetEnabled: twEnabled('billabletargetmet'),
    burndownchartLimit: twValue('burndownchart', normalizeFeatureLimit),
    calendarGoogleCalSyncEnabled: twEnabled('googlecalsync'),
    clockInOutEnabled: twEnabled('clockinout'),
    customItemsEnabled: twEnabled('customitems'),
    customfieldsLimit: twValue('customfields', normalizeFeatureLimit),
    customfieldsProjectsEnabled: twEnabled('customfieldsprojects'),
    customfieldsTasksEnabled: twEnabled('customfieldstasks'),
    financialBudgetLimit: twValue('projectfinancialbudgets', normalizeFeatureLimit),
    myBoardsEnabled: twEnabled('myboards'),
    newGanttEnabled: twEnabled('newgantt'),
    newSchedulerTentativeProjects: twEnabled('newschedulertentativeprojects'),
    newSchedulerTentativeProjectsLimit: twValue('newschedulertentativeprojects', Number),
    portfolioLimit: twValue('portfolio', normalizeFeatureLimit),
    plannedVsActualMilestoneReportEnabled: twEnabled('plannedactualmilestonereport'),
    plannedVsActualTasksReportEnabled: twEnabled('plannedactualtasksreport'),
    profitabilityEnabled: twEnabled('profitability'),
    projectBudgetExpensesEnabled: twEnabled('projectbudgetexpenses'),
    projectFinancialBudgetsEnabled: twEnabled('projectfinancialbudgets'),
    projectTemplatesEnabled: twEnabled('projecttemplates'),
    projectTemplatesLimit: twValue('projecttemplates', normalizeFeatureLimit),
    projectTimeBudgetsEnabled: twEnabled('projecttimebudgets'),
    projectTimeReportEnabled: twEnabled('projecttimereport'),
    projectRetainerBudgetsEnabled: twEnabled('retainerbudgets'),
    reportsEnabled: twEnabled('reports'),
    projectsHealthReportEnabled: twEnabled('projectshealthreport'),
    salesforceIntegrationEnabled: twEnabled('salesforceintegration'),
    hubSpotIntegrationEnabled: twEnabled('hubspot'),
    quickbooksBasicIntegrationEnabled: twEnabled('quickbooksbasic'),
    sharePointIntegrationEnabled: twEnabled('sharepoint'),
    tasklistBudgetsEnabled: twEnabled('tasklistbudgets'),
    tasklistBudgetsLimit: twValue('tasklistbudgets', normalizeFeatureLimit),
    timeBudgetLimit: twValue('projecttimebudgets', normalizeFeatureLimit),
    anyBudgetsEnabled: twEnabled('budgets'),
    anyBudgetsLimit: twValue('budgets', normalizeFeatureLimit),
    taskTimeReportEnabled: twEnabled('tasktimereport'),
    retainerBudgetLimit: twValue('retainerbudgets', normalizeFeatureLimit),
    fixedFeeBudgetsEnabled: twEnabled('projectfixedfeebudgets'),
    fixedFeeBudgetLimit: twValue('projectfixedfeebudgets', normalizeFeatureLimit),
    timeAndMaterialsBudgetsEnabled: twEnabled('projecttimeandmaterialsbudgets'),
    userLoggedTimeReportEnabled: twEnabled('userloggedtimereport'),
    userTaskCompletionReportEnabled: twEnabled('usertaskcompletionreport'),
    utilizationReportEnabled: twEnabled('utilizationreport'),
    timeReportEnabled: twEnabled('timereport'),
    workloadBacklogEnabled: twEnabled('workloadbacklog'),
    previewProfitabilityEnabled: twEnabled('previewprofitability'),
    customReportingEnabled: twEnabled('customreporting'),
    customReportingLimit: twValue('customreporting', normalizeFeatureLimit),
    workloadPlannerEnabled: twEnabled('workloadplanner'),
    intakeFormsLimit: twValue('intakeforms', normalizeFeatureLimit),
    riskRegisterEnabled: twEnabled('riskregister'),
    timeRemindersEnabled: twEnabled('timereminders'),
    timeApprovalsEnabled: twEnabled('timeapprovals'),
    clientUsersEnabled: twEnabled('clientusers'),
    jobRolesEnabled: twEnabled('jobroles'),
    scheduleReportingEnabled: twEnabled('schedulereporting'),
    resourceSchedulePeopleEnabled: twEnabled('resourceschedulingpeople'),
    googleSheetsExportEnabled: twEnabled('accessgooglesheetsexports'),
    skillsEnabled: twEnabled('skills'),
    publicHolidaysEnabled: twEnabled('publicholidays'),
    quotesFeatureEnabled: twEnabled('quotes'),
    planningCalendarSyncEnabled: twEnabled('planningcalendarsync'),
    teamsGlobalTeamsEnabled: twEnabled('teamsglobalteams'),
    multicurrencyEnabled: twEnabled('multicurrency'),
    installationRatesEnabled: twEnabled('installationrates'),

    // Teamwork AI features
    aiEnabled: twAiEnabled(''),
    aiTaskBuilderEnabled: twAiEnabled('task_builder'),
    aiTextEditorAssistantEnabled: twAiEnabled('comment_assistant'),
    aiProjectBuilderEnabled: twAiEnabled('project_builder'),
    aiUtilizationSummaryEnabled: twAiEnabled('utilization_summary'),
  };
}

/**
 * Provides LaunchDarkly and Teamwork features.
 * @returns {void}
 */
export function provideFeatures() {
  provide(useFeaturesSymbol, FeaturesService());
}

/**
 * Provides LaunchDarkly and Teamwork features.
 * @returns {ReturnType<typeof FeaturesService>}
 */
export function useFeatures() {
  return inject(useFeaturesSymbol);
}
